import {createApi} from '@reduxjs/toolkit/query/react'
import type {
  AnomalyDetectionConfiguration,
  AnomalyDetectionConfigurationCreate,
  CreateProgramStrategyInstanceResponse,
  PaginatedProgramStrategyInstanceList,
  PaginatedProgramStrategyList,
  ProgramStrategy,
  ProgramStrategyInstanceModel,
} from '@interfaces/anomalyDetection'
import {createAnomalyDetectionBaseQuery} from '@apis/helpers.ts'

export const anomalyDetectionAPI = createApi({
  reducerPath: 'anomalyDetectionAPI',
  // In-core AD in mbai-pm-core, /core/-routed (AB#5658).
  baseQuery: createAnomalyDetectionBaseQuery(),
  tagTypes: ['AnomalyDetectionConfiguration', 'ProgramStrategyInstance', 'ProgramStrategy'],
  endpoints: (builder) => ({
    // Anomaly Detection Configuration Create
    createAnomalyDetectionConfig: builder.mutation<
      AnomalyDetectionConfiguration,
      AnomalyDetectionConfigurationCreate
    >({
      query: (body) => ({
        url: 'anomaly_detection_configurations/',
        method: 'POST',
        body,
      }),
    }),

    // Program Strategy Instance List
    getProgramStrategyInstances: builder.query<
      PaginatedProgramStrategyInstanceList,
      {
        programStrategyUuid: string
        limit?: number
        offset?: number
      }
    >({
      query: ({programStrategyUuid, limit, offset}) => {
        let url = `program_strategies/${programStrategyUuid}/program_strategy_instances/`
        const params = new URLSearchParams()
        if (limit) {
          params.append('limit', limit.toString())
        }
        if (offset) {
          params.append('offset', offset.toString())
        }
        const queryString = params.toString()
        if (queryString) {
          url += `?${queryString}`
        }
        return {url}
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({uuid}) => ({
                type: 'ProgramStrategyInstance' as const,
                id: uuid,
              })),
              {type: 'ProgramStrategyInstance', id: 'LIST'},
            ]
          : [{type: 'ProgramStrategyInstance', id: 'LIST'}],
    }),

    // Program Strategy Instance Create
    createProgramStrategyInstance: builder.mutation<
      CreateProgramStrategyInstanceResponse,
      {
        programStrategyUuid: string
        body: {
          batches: Array<{
            core_batch_uuid: string
            t_start: string
            t_end: string
          }>
          training_initiated_by?: string
          hyperparameters?: Array<{
            name: string
            value: string
          }>
        }
      }
    >({
      query: ({programStrategyUuid, body}) => ({
        url: `program_strategies/${programStrategyUuid}/program_strategy_instances/`,
        method: 'POST',
        body,
      }),
    }),

    // Program Strategy Instance Get
    getProgramStrategyInstance: builder.query<
      ProgramStrategyInstanceModel,
      {
        programStrategyUuid: string
        instanceUuid: string
        sampling?: {
          type?: 'auto' | 'finest' | string
          maxPoints?: number
        }
      }
    >({
      query: ({programStrategyUuid, instanceUuid, sampling}) => {
        let url = `program_strategies/${programStrategyUuid}/program_strategy_instances/${instanceUuid}/`

        const params = new URLSearchParams()
        if (sampling) {
          if (sampling.type) {
            params.append('t_sampling', sampling.type)
          }
          if (sampling.maxPoints) {
            params.append('max_points', sampling.maxPoints.toString())
          }
        } else {
          // Default to auto sampling if none provided
          params.append('t_sampling', 'auto')
        }

        const queryString = params.toString()
        if (queryString) {
          url += `?${queryString}`
        }

        return {url}
      },
    }),

    getProgramStrategyInstanceByUuid: builder.query<
      ProgramStrategyInstanceModel,
      {
        instanceUuid: string
      }
    >({
      query: ({instanceUuid}) => ({
        url: `program_strategy_instances/${instanceUuid}/`,
      }),
      providesTags: (_result, _error, {instanceUuid}) => [
        {type: 'ProgramStrategyInstance', id: instanceUuid},
      ],
    }),

    // Program Strategy Instance Patch (Set as Favorite)
    updateProgramStrategyInstance: builder.mutation<
      ProgramStrategyInstanceModel,
      {
        programStrategyUuid: string
        instanceUuid: string
        favorite: boolean
      }
    >({
      query: ({programStrategyUuid, instanceUuid, favorite}) => ({
        url: `program_strategies/${programStrategyUuid}/program_strategy_instances/${instanceUuid}/`,
        method: 'PATCH',
        body: {favorite},
      }),
      invalidatesTags: (_result, _error, {instanceUuid, programStrategyUuid}) => [
        {type: 'ProgramStrategyInstance', id: instanceUuid},
        {type: 'ProgramStrategyInstance', id: 'LIST'},
        {type: 'ProgramStrategy', id: programStrategyUuid},
      ],
    }),

    // Program Strategy List
    getProgramStrategies: builder.query<
      PaginatedProgramStrategyList,
      {
        coreProgramUuid: string
        limit?: number
        offset?: number
      }
    >({
      query: ({coreProgramUuid, limit, offset}) => {
        let url = `programs/${coreProgramUuid}/program_strategies/`
        const params = new URLSearchParams()
        if (limit) {
          params.append('limit', limit.toString())
        }
        if (offset) {
          params.append('offset', offset.toString())
        }
        const queryString = params.toString()
        if (queryString) {
          url += `?${queryString}`
        }
        return {url}
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map((strategy) => ({
                type: 'ProgramStrategy' as const,
                id: strategy.uuid,
              })),
              {type: 'ProgramStrategy', id: 'LIST'},
            ]
          : [{type: 'ProgramStrategy', id: 'LIST'}],
    }),

    // Program Strategy Details
    getProgramStrategy: builder.query<
      ProgramStrategy,
      {
        coreProgramUuid: string
        strategyUuid: string
      }
    >({
      query: ({coreProgramUuid, strategyUuid}) => ({
        url: `programs/${coreProgramUuid}/program_strategies/${strategyUuid}/`,
      }),
      providesTags: (_result, _error, {strategyUuid}) => [
        {type: 'ProgramStrategy', id: strategyUuid},
      ],
    }),

    // Program Strategy Patch
    updateProgramStrategy: builder.mutation<
      ProgramStrategy,
      {
        coreProgramUuid: string
        strategyUuid: string
        body: {
          name?: string
          active?: boolean
          min_batches_for_training?: number | null
          strategy?: number
        }
      }
    >({
      query: ({coreProgramUuid, strategyUuid, body}) => ({
        url: `programs/${coreProgramUuid}/program_strategies/${strategyUuid}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {strategyUuid}) => [
        {type: 'ProgramStrategy', id: strategyUuid},
      ],
    }),
  }),
})

export const {
  useCreateAnomalyDetectionConfigMutation,
  useGetProgramStrategyInstancesQuery,
  useCreateProgramStrategyInstanceMutation,
  useGetProgramStrategyInstanceQuery,
  useGetProgramStrategyInstanceByUuidQuery,
  useUpdateProgramStrategyInstanceMutation,
  useGetProgramStrategiesQuery,
  useGetProgramStrategyQuery,
  useUpdateProgramStrategyMutation,
} = anomalyDetectionAPI
